const FULL_VIN_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/;
const OCR_VIN_CHARS_REGEX = /^[A-Z0-9]{17}$/;

const VIN_TRANSLITERATION = {
  A: 1,
  B: 2,
  C: 3,
  D: 4,
  E: 5,
  F: 6,
  G: 7,
  H: 8,
  J: 1,
  K: 2,
  L: 3,
  M: 4,
  N: 5,
  P: 7,
  R: 9,
  S: 2,
  T: 3,
  U: 4,
  V: 5,
  W: 6,
  X: 7,
  Y: 8,
  Z: 9,
};

const VIN_WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];
const VIN_LABEL_COMPACT_REGEX =
  /(VIN|V1N|VIM|VEHICLEIDENTIFICATION(NUMBER|NO)?|IDENTIFICATION(NUMBER|NO)?|VINNO|VINNUMBER|VINNUM|VINN0)/;

const normalizeVinCandidate = (value = "") =>
  String(value || "")
    .toUpperCase()
    .replace(/O|Q/g, "0")
    .replace(/I/g, "1")
    .replace(/[^A-HJ-NPR-Z0-9]/g, "");

const compactForLabelSearch = (value = "") =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

const normalizeLines = (text = "") =>
  String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

const getVinCharValue = (char) => {
  if (/^\d$/.test(char)) return Number(char);
  return VIN_TRANSLITERATION[char] ?? null;
};

export const getVinCheckDigit = (vin = "") => {
  const safeVin = String(vin || "").toUpperCase();
  if (!FULL_VIN_REGEX.test(safeVin)) return "";

  const total = safeVin.split("").reduce((sum, char, index) => {
    const value = getVinCharValue(char);
    return sum + value * VIN_WEIGHTS[index];
  }, 0);
  const remainder = total % 11;
  return remainder === 10 ? "X" : String(remainder);
};

export const isVinCheckDigitValid = (vin = "") => {
  const safeVin = String(vin || "").toUpperCase();
  return FULL_VIN_REGEX.test(safeVin) && safeVin[8] === getVinCheckDigit(safeVin);
};

const hasVinLabel = (value = "") =>
  VIN_LABEL_COMPACT_REGEX.test(compactForLabelSearch(value));

const findLabelEndIndex = (line = "") => {
  const match = String(line || "").match(
    /(?:V\s*[.:-]?\s*[I1]\s*[.:-]?\s*N|VEHICLE\s+IDENTIFICATION\s+(?:NUMBER|NO\.?)?|IDENTIFICATION\s+(?:NUMBER|NO\.?)?)\s*(?:NO\.?|NUMBER|NUM|#|:)?/i,
  );
  return match ? match.index + match[0].length : -1;
};

const extractValidVinWindows = (text = "", { correctOcr = true } = {}) => {
  const compact = String(text || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
  const candidates = new Set();

  for (let index = 0; index <= compact.length - 17; index += 1) {
    const raw = compact.slice(index, index + 17);
    if (!OCR_VIN_CHARS_REGEX.test(raw)) continue;

    const normalized = correctOcr
      ? normalizeVinCandidate(raw)
      : raw.replace(/[^A-HJ-NPR-Z0-9]/g, "");
    if (normalized.length === 17 && isVinCheckDigitValid(normalized)) {
      candidates.add(normalized);
    }
  }

  return [...candidates];
};

const addCandidates = (results, text, score, source) => {
  extractValidVinWindows(text).forEach((vin) => {
    const existing = results.get(vin);
    if (!existing || score > existing.score) {
      results.set(vin, { vin, score, source });
    }
  });
};

export const findVinCandidatesFromOcrText = (text = "") => {
  const lines = normalizeLines(text);
  const results = new Map();

  lines.forEach((line, index) => {
    const labelEndIndex = findLabelEndIndex(line);
    const nextLine = lines[index + 1] || "";
    const secondNextLine = lines[index + 2] || "";

    if (labelEndIndex >= 0 || hasVinLabel(line)) {
      const sameLineText =
        labelEndIndex >= 0 ? line.slice(labelEndIndex) : line;
      addCandidates(results, sameLineText, 120, "same-line-label");
      addCandidates(results, `${sameLineText} ${nextLine}`, 105, "label-next-line");
      addCandidates(
        results,
        `${sameLineText} ${nextLine} ${secondNextLine}`,
        95,
        "label-nearby-lines",
      );
      return;
    }

    addCandidates(results, line, 40, "line");
  });

  const ranked = [...results.values()].sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.vin.localeCompare(b.vin);
  });

  if (ranked.some((candidate) => candidate.score >= 95)) {
    return ranked.filter((candidate) => candidate.score >= 95);
  }

  return ranked.length === 1 ? ranked : [];
};

export const extractVinFromOcrText = (text = "") =>
  findVinCandidatesFromOcrText(text)[0]?.vin || "";

export const extractVinFromBarcodeText = (text = "") =>
  extractValidVinWindows(text, { correctOcr: false })[0] || "";
